import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, ExternalLink, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { api, downloadCsv, rp } from "@/lib/api";

const BUCKETS = {
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  open: "bg-amber-100 text-amber-800 border-amber-200",
  failed: "bg-red-100 text-red-700 border-red-200",
  refund: "bg-purple-100 text-purple-800 border-purple-200",
};
const LABEL = { paid: "Lunas", open: "Menunggu", failed: "Gagal", refund: "Refund" };
const dt = (v) => (v ? `${v.slice(0, 10)} ${v.slice(11, 16)}` : "-");

const Card = ({ label, value, sub, testid }) => (
  <div className="bg-white border border-black/10 rounded-lg p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
    <div className="mt-1.5 text-xl md:text-2xl font-semibold tabular-nums">{value}</div>
    {sub && <div className="mt-1 text-[11px] text-ink-soft truncate">{sub}</div>}
  </div>
);

export default function OnlinePayments() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ env: "", status: "", q: "", date_from: "", date_to: "", include_test: false });
  const [busy, setBusy] = useState("");

  const load = useCallback((filters) => {
    const p = filters || {};
    return api.get("/payments/online/history", { params: { ...p, limit: 200 } })
      .then((r) => setD(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat riwayat"));
  }, []);

  useEffect(() => {
    load({
      env: f.env,
      status: f.status,
      date_from: f.date_from,
      date_to: f.date_to,
      include_test: f.include_test,
    });
  }, [f.date_from, f.date_to, f.env, f.include_test, f.status, load]);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const checkAll = async () => {
    setBusy("all");
    try {
      const r = await api.post("/payments/online/check-all", { scope: "open", include_test: f.include_test });
      toast.success(r.data.message);
      await load(f);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal cek status"); }
    finally { setBusy(""); }
  };

  const checkOne = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/payments/online/check/${encodeURIComponent(id)}`);
      toast.success(r.data.found ? `Status di Midtrans: ${r.data.midtrans_status}` : r.data.message);
      await load(f);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal cek status"); }
    finally { setBusy(""); }
  };

  if (!d) return <div className="text-ink-soft">Memuat riwayat pembayaran…</div>;
  const s = d.summary;

  return (
    <div className="space-y-6" data-testid="admin-online-payments">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Riwayat Pembayaran Online</h1>
          <p className="mt-2 text-sm text-ink-soft max-w-2xl">
            Semua percobaan bayar lewat Midtrans. Kalau webhook tidak masuk, tekan
            <span className="text-ink font-medium"> Cek semua yang menunggu</span> — status ditanyakan
            langsung ke Midtrans lalu booking diselaraskan.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="op-check-all" disabled={busy === "all"} onClick={checkAll}
            className="h-10 px-4 rounded-md lm-btn-primary text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            <RefreshCw size={14} className={busy === "all" ? "animate-spin" : ""} /> Cek semua yang menunggu
          </button>
          <button data-testid="op-csv"
            onClick={() => downloadCsv("/payments/online/export.csv", f, "riwayat-pembayaran-online.csv")
              .then(() => toast.success("CSV diunduh")).catch(() => toast.error("Gagal mengunduh CSV"))}
            className="h-10 px-4 rounded-md border border-black/15 text-sm inline-flex items-center gap-2">
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      {d.keys.active_env === "sandbox" && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900"
          data-testid="op-sandbox-label">
          SANDBOX — pembayaran uji coba, bukan uang nyata
        </div>
      )}

      <div className="bg-white border border-black/10 rounded-lg p-4 text-xs flex flex-wrap gap-x-6 gap-y-2"
        data-testid="op-keys">
        <span className="inline-flex items-center gap-1.5 font-medium"><ShieldCheck size={13} /> Environment aktif: {d.keys.active_env}</span>
        <span>Kunci production: {d.keys.production_ready ? "terpasang" : "belum"}</span>
        <span>Kunci sandbox: {d.keys.sandbox_ready ? "terpasang" : "belum"}</span>
        <span>Checkout publik: {d.keys.public_enabled ? "AKTIF" : "nonaktif"}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Lunas" value={s.paid} sub={rp(s.paid_total)} testid="op-card-paid" />
        <Card label="Menunggu" value={s.open} sub={rp(s.pending_total)} testid="op-card-open" />
        <Card label="Gagal / Kedaluwarsa" value={s.failed} testid="op-card-failed" />
        <Card label="Refund" value={s.refund} sub={`total ${d.totals.all} transaksi`} testid="op-card-refund" />
      </div>

      <div className="bg-white border border-black/10 rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3.5 text-ink-soft" />
          <input data-testid="op-search" placeholder="Order ID / booking / transaction id"
            value={f.q} onChange={(e) => set("q", e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(f)}
            className="h-10 w-64 rounded-md border border-input bg-white pl-9 pr-3 text-sm" />
        </div>
        <select data-testid="op-filter-status" value={f.status} onChange={(e) => set("status", e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">Semua status</option>
          <option value="paid">Lunas</option>
          <option value="open">Menunggu</option>
          <option value="failed">Gagal / kedaluwarsa</option>
          <option value="refund">Refund</option>
        </select>
        <select data-testid="op-filter-env" value={f.env} onChange={(e) => set("env", e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">Semua environment</option>
          <option value="production">Production</option>
          <option value="sandbox">Sandbox</option>
        </select>
        <input type="date" data-testid="op-date-from" value={f.date_from}
          onChange={(e) => set("date_from", e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm" />
        <input type="date" data-testid="op-date-to" value={f.date_to}
          onChange={(e) => set("date_to", e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm" />
        <label className="text-xs inline-flex items-center gap-2">
          <input type="checkbox" data-testid="op-include-test" checked={f.include_test}
            onChange={(e) => set("include_test", e.target.checked)} />
          Tampilkan data uji ({d.totals.test})
        </label>
        <button data-testid="op-apply" onClick={() => load(f)}
          className="h-10 px-4 rounded-md border border-black/15 text-sm">Terapkan</button>
      </div>

      <div className="bg-white border border-black/10 rounded-lg overflow-hidden" data-testid="op-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-black/[0.03] text-ink-soft">
              <tr>
                {["Order ID", "Customer", "Nominal", "Status", "Metode", "Env", "Dibuat", ""].map((h) => (
                  <th key={h} className="text-left font-medium px-3 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {d.rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-ink-soft" data-testid="op-empty">
                  Belum ada percobaan pembayaran online.
                </td></tr>
              )}
              {d.rows.map((r) => (
                <tr key={r.attempt_order_id} className="hover:bg-black/[0.02]"
                  data-testid={`op-row-${r.attempt_order_id}`}>
                  <td className="px-3 py-2.5 font-mono text-[11px] whitespace-nowrap">
                    {r.attempt_order_id}
                    {r.is_test && <span className="ml-1.5 text-[10px] text-lm-red">UJI</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="truncate max-w-[180px]">{r.booking.customer_name || "—"}</div>
                    <div className="text-ink-soft">{r.booking.rental_date || r.booking_code}</div>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums whitespace-nowrap">{rp(r.amount)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] ${BUCKETS[r.bucket]}`}>
                      {LABEL[r.bucket]}
                    </span>
                    <div className="text-ink-soft mt-0.5">{r.status || "—"}</div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.payment_type || "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.env}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-ink-soft">{dt(r.created_at)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-right">
                    <button data-testid={`op-check-${r.attempt_order_id}`} disabled={busy === r.attempt_order_id}
                      onClick={() => checkOne(r.attempt_order_id)}
                      className="px-2.5 py-1 rounded-md border border-black/15 mr-1.5 disabled:opacity-50">
                      Cek
                    </button>
                    {r.booking.exists && (
                      <Link to={`/admin/bookings/${r.booking_id}`} data-testid={`op-open-${r.attempt_order_id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-black/15 hover:text-lm-red">
                        <ExternalLink size={11} /> Booking
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
